import { nextTick } from 'vue';
import { createI18n } from 'vue-i18n';
import { useLanguage } from '../composables/useLanguage';
// ─── AR locale files ──────────────────────────────────────────────────────────
import arApp from './locales/ar/app.json'; // auth, home, dashboard, profile, wallet, …
import arBox from './locales/ar/box.json'; // box management, shipping, status, limit
import arCollection from './locales/ar/collection.json'; // member-answerable collection issues (decisions-required)
import arCollector from './locales/ar/collector.json'; // the collector's day (STAFF copy, collector-app phase 1)
import arCommon from './locales/ar/common.json'; // tabs, general, filters, errors, governorates
import arItem from './locales/ar/item.json'; // item fields, status, reservation, issue
import arNotification from './locales/ar/notification.json'; // notification titles/bodies + preference-row labels
import arPoola from './locales/ar/poola.json'; // Poola tab (teaser + offers channel)
import arProduct from './locales/ar/product.json'; // product domain (feed, AI enhance, add-to-box)
import arSeller from './locales/ar/seller.json'; // seller club, onboarding, registration
import arSupplier from './locales/ar/supplier.json'; // supplier wall (demand feed, claim/withdraw)
import arVariant from './locales/ar/variant.json'; // variant editor UI
// ─── EN locale files ──────────────────────────────────────────────────────────
import enApp from './locales/en/app.json';
import enBox from './locales/en/box.json';
import enCollection from './locales/en/collection.json';
import enCollector from './locales/en/collector.json';
import enCommon from './locales/en/common.json';
import enItem from './locales/en/item.json';
import enNotification from './locales/en/notification.json';
import enPoola from './locales/en/poola.json'; // Poola tab (teaser + offers channel)
import enProduct from './locales/en/product.json';
import enSeller from './locales/en/seller.json';
import enSupplier from './locales/en/supplier.json';
import enVariant from './locales/en/variant.json';
import { arabicPlural } from './pluralRules';
import { deepMerge } from '@/utils/utils.js';

const { currentLang } = useLanguage();

/**
 * IMPORTANT: Use lodash `merge()` — NOT spread (`...`).
 * Spread is a shallow merge: if two files share a top-level key (e.g. "product"),
 * the last file's object silently overwrites the first, deleting all nested keys.
 * `merge()` recursively deep-merges, so every file's keys are preserved.
 */
const i18n = createI18n({
  legacy: false,
  locale: currentLang.value,
  fallbackLocale: 'ar',
  // vue-i18n has no Arabic plural rule of its own, so without this the six-form
  // strings (box.totals.lineCount, box.totals.openCarton) read «منتجان» for any
  // count from 3 up. It claims only six-form messages and hands every other shape
  // back to the default — see pluralRules.js for why, and for the guard test.
  pluralRules: { ar: arabicPlural },
  messages: {
    ar: deepMerge(arCommon, arApp, arProduct, arBox, arItem, arVariant, arSeller, arSupplier, arNotification, arPoola, arCollection, arCollector),
    en: deepMerge(enCommon, enApp, enProduct, enBox, enItem, enVariant, enSeller, enSupplier, enNotification, enPoola, enCollection, enCollector),
  },
  globalInjection: true,
});

/** Lazy-load a single locale module at runtime (e.g. for code-split routes). */
export async function loadLocaleMessages(locale, module) {
  const messages = await import(`./locales/${locale}/${module}.json`);
  i18n.global.mergeLocaleMessage(locale, messages.default);
  return nextTick();
}

export default i18n;
